(() => {
  const ROOT_SELECTOR = '[data-bundle-mobile-prototype]';
  const pendingIntents = new Map();

  const formatMoney = (amount, currency) =>
    new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency,
    }).format(amount / 100);

  const visibleVariantTitle = (title) =>
    title
      .split(' / ')
      .filter((value) => value.toLowerCase() !== 'not included')
      .join(' / ');

  const syncStickyBar = (root, variantId) => {
    const variants = JSON.parse(root.dataset.variants || '[]');
    const variant = variants.find((item) => String(item.id) === String(variantId));
    if (!variant) return;

    const input = root.querySelector('[data-bundle-mobile-variant-id]');
    const title = root.querySelector('[data-bundle-mobile-title]');
    const price = root.querySelector('[data-bundle-mobile-price]');
    const submit = root.querySelector('[data-bundle-mobile-submit]');
    const buyNow = root.querySelector('[data-bundle-mobile-buy-now]');
    const label = root.querySelector('[data-bundle-mobile-submit-label]');

    input.value = variant.id;
    input.disabled = !variant.available;
    title.textContent = visibleVariantTitle(variant.public_title || variant.title);
    price.textContent = formatMoney(variant.price, root.dataset.currency);
    submit.disabled = !variant.available;
    buyNow.disabled = !variant.available;
    label.textContent = variant.available ? window.variantStrings.addToCart : window.variantStrings.soldOut;
  };

  const syncOptionSheet = (root) => {
    root.querySelectorAll('[data-bundle-mobile-option-value-id]').forEach((sheetInput) => {
      const sourceInput = document.querySelector(
        `variant-selects input[data-option-value-id="${sheetInput.dataset.bundleMobileOptionValueId}"]`,
      );
      if (!sourceInput) return;

      sheetInput.checked = sourceInput.checked;
    });

    updateOptionPresentation(root);
  };

  const setDialogIntent = (root, intent) => {
    root.dataset.bundleMobileIntent = intent;
    pendingIntents.set(root.dataset.sectionId, intent);
    const apply = root.querySelector('[data-bundle-mobile-apply]');
    if (!apply) return;
    apply.textContent = intent === 'buy-now' ? apply.dataset.buyNowLabel : apply.dataset.optionsLabel;
  };

  const buySelectedVariantNow = async (root) => {
    const variantInput = root.querySelector('[data-bundle-mobile-variant-id]');
    const apply = root.querySelector('[data-bundle-mobile-apply]');
    const buyNow = root.querySelector('[data-bundle-mobile-buy-now]');
    if (!variantInput?.value || variantInput.disabled || !apply || !buyNow) return;

    apply.disabled = true;
    buyNow.disabled = true;
    apply.setAttribute('aria-busy', 'true');

    const rootPath = window.Shopify?.routes?.root || '/';
    try {
      pendingIntents.delete(root.dataset.sectionId);
      const response = await fetch(`${rootPath}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantInput.value), quantity: 1 }],
        }),
      });
      if (!response.ok) throw new Error('Unable to add selected variant');
      window.location.assign(`${rootPath}checkout`);
    } catch (error) {
      apply.disabled = false;
      buyNow.disabled = false;
      apply.removeAttribute('aria-busy');
      const wrapper = root.querySelector('.product-form__error-message-wrapper');
      const message = root.querySelector('.product-form__error-message');
      if (wrapper && message) {
        message.textContent = error.message;
        wrapper.hidden = false;
      }
    }
  };

  const updateOptionPresentation = (root) => {
    const variants = JSON.parse(root.dataset.variants || '[]');
    const groups = [...root.querySelectorAll('[data-bundle-mobile-option-group]')];
    const setGroup = groups.find((group) => group.dataset.optionName.toLowerCase().includes('set'));
    if (!setGroup) {
      groups.forEach((group) => {
        group.hidden = false;
        const optionPosition = Number(group.dataset.optionPosition) - 1;
        group.querySelectorAll('[data-bundle-mobile-option-value-id]').forEach((input) => {
          const label = group.querySelector(`label[for="${input.id}"]`);
          const soldOut = label?.querySelector('[data-bundle-mobile-sold-out]');
          const matchingVariants = variants.filter((variant) => variant.options?.[optionPosition] === input.value);
          const exists = matchingVariants.length > 0;
          const available = matchingVariants.some((variant) => variant.available);

          input.hidden = !exists;
          label.hidden = !exists;
          input.disabled = exists && !available;
          if (soldOut) soldOut.hidden = available || !exists;
        });
      });
      return;
    }

    const selectedSet = setGroup?.querySelector('[data-bundle-mobile-option-value-id]:checked')?.value || '';
    const setPosition = Number(setGroup?.dataset.optionPosition || 1) - 1;
    const setIncludesNecklace = selectedSet.toLowerCase().includes('necklace');
    const setIncludesEarrings = selectedSet.toLowerCase().includes('earring');
    groups.forEach((group) => {
      const groupName = group.dataset.optionName.toLowerCase();
      const isNecklace = groupName.includes('necklace');
      const isEarring = groupName.includes('earring');
      const relevant = (!isNecklace || setIncludesNecklace) && (!isEarring || setIncludesEarrings);
      group.hidden = !relevant;
      if (!relevant) return;

      const optionPosition = Number(group.dataset.optionPosition) - 1;
      group.querySelectorAll('[data-bundle-mobile-option-value-id]').forEach((input) => {
        const label = group.querySelector(`label[for="${input.id}"]`);
        const soldOut = label?.querySelector('[data-bundle-mobile-sold-out]');
        const isNotIncluded = input.value.toLowerCase() === 'not included';
        const hideNotIncluded = isNotIncluded;

        const candidateSet = group === setGroup ? input.value : selectedSet;
        const matchingVariants = variants.filter(
          (variant) =>
            variant.options?.[setPosition] === candidateSet &&
            variant.options?.[optionPosition] === input.value,
        );
        const exists = matchingVariants.length > 0;
        const available = matchingVariants.some((variant) => variant.available);

        input.hidden = hideNotIncluded || !exists;
        label.hidden = hideNotIncluded || !exists;
        input.disabled = exists && !available;
        if (soldOut) soldOut.hidden = available || !exists;
      });
    });
  };

  const selectNotIncludedForExcludedGroups = (root) => {
    root.querySelectorAll('[data-bundle-mobile-option-group][hidden]').forEach((group) => {
      const notIncluded = [...group.querySelectorAll('[data-bundle-mobile-option-value-id]')].find(
        (input) => input.value.toLowerCase() === 'not included',
      );
      if (!notIncluded) return;
      const sourceInput = document.querySelector(
        `variant-selects input[data-option-value-id="${notIncluded.dataset.bundleMobileOptionValueId}"]`,
      );
      if (sourceInput && !sourceInput.checked) sourceInput.click();
    });
  };

  const applySheetSelectionToNativePicker = (root) => {
    updateOptionPresentation(root);

    const groups = [...root.querySelectorAll('[data-bundle-mobile-option-group]')];
    groups.forEach((group) => {
      let selected = group.querySelector('[data-bundle-mobile-option-value-id]:checked');

      if (group.hidden) {
        selected = [...group.querySelectorAll('[data-bundle-mobile-option-value-id]')].find(
          (input) => input.value.toLowerCase() === 'not included',
        );
      } else if (
        !selected ||
        selected.hidden ||
        selected.disabled ||
        selected.value.toLowerCase() === 'not included'
      ) {
        selected = [...group.querySelectorAll('[data-bundle-mobile-option-value-id]')].find(
          (input) => !input.hidden && !input.disabled && input.value.toLowerCase() !== 'not included',
        );
      }

      if (!selected) return;
      selected.checked = true;
      const sourceInput = document.querySelector(
        `variant-selects input[data-option-value-id="${selected.dataset.bundleMobileOptionValueId}"]`,
      );
      if (sourceInput) sourceInput.checked = true;
    });

    const sourceTrigger = document.querySelector('variant-selects input[type="radio"]:checked');
    sourceTrigger?.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const syncNativeDesktopPicker = (root, selectTechnicalDefaults = false) => {
    if (!window.matchMedia('(min-width: 750px)').matches) return;

    const variants = JSON.parse(root.dataset.variants || '[]');
    const picker = root.closest('product-info')?.querySelector('variant-selects');
    if (!picker) return;

    const groups = [...picker.querySelectorAll('.product-form__input:not([data-bundle-bracelet-info])')];
    const groupName = (group) => group.querySelector('.form__label')?.textContent.trim() || '';
    const setGroup = groups.find((group) => groupName(group).toLowerCase().includes('set'));
    if (!setGroup) {
      groups.forEach((group, groupIndex) => {
        group.hidden = false;
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          const label = group.querySelector(`label[for="${input.id}"]`);
          if (!label) return;

          label.querySelector('[data-native-sold-out]')?.remove();
          const matches = variants.filter((variant) => variant.options?.[groupIndex] === input.value);
          const exists = matches.length > 0;
          const available = matches.some((variant) => variant.available);

          input.hidden = !exists;
          label.hidden = !exists;
          input.disabled = exists && !available;
          label.classList.toggle('bundle-option-sold-out', exists && !available);
          if (exists && !available) {
            const status = document.createElement('small');
            status.dataset.nativeSoldOut = '';
            status.textContent = window.variantStrings.soldOut;
            label.append(status);
          }
        });
      });
      return;
    }

    const selectedSet = setGroup?.querySelector('input[type="radio"]:checked')?.value || '';
    const setIncludesNecklace = selectedSet.toLowerCase().includes('necklace');
    const setIncludesEarrings = selectedSet.toLowerCase().includes('earring');

    groups.forEach((group, groupIndex) => {
      const name = groupName(group).toLowerCase();
      const isNecklace = name.includes('necklace');
      const isEarring = name.includes('earring');
      const relevant = (!isNecklace || setIncludesNecklace) && (!isEarring || setIncludesEarrings);
      group.hidden = !relevant;

      const technicalDefault = [...group.querySelectorAll('input[type="radio"]')].find(
        (input) => input.value.toLowerCase() === 'not included',
      );
      if (!relevant && selectTechnicalDefaults && technicalDefault && !technicalDefault.checked) {
        technicalDefault.click();
      }

      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        const label = group.querySelector(`label[for="${input.id}"]`);
        if (!label) return;

        label.querySelector('[data-native-sold-out]')?.remove();
        const isNotIncluded = input.value.toLowerCase() === 'not included';
        const candidateSet = group === setGroup ? input.value : selectedSet;
        const matches = variants.filter(
          (variant) =>
            variant.options?.[0] === candidateSet &&
            variant.options?.[groupIndex] === input.value,
        );
        const exists = matches.length > 0;
        const available = matches.some((variant) => variant.available);

        input.hidden = isNotIncluded || !exists;
        label.hidden = isNotIncluded || !exists;
        input.disabled = exists && !available;
        label.classList.toggle('bundle-option-sold-out', exists && !available);
        if (exists && !available) {
          const status = document.createElement('small');
          status.dataset.nativeSoldOut = '';
          status.textContent = window.variantStrings.soldOut;
          label.append(status);
        }
      });

      if (relevant && selectTechnicalDefaults && technicalDefault?.checked) {
        const firstAvailable = [...group.querySelectorAll('input[type="radio"]')].find(
          (input) => input.value.toLowerCase() !== 'not included' && !input.hidden && !input.disabled,
        );
        firstAvailable?.click();
      }
    });
  };

  document.addEventListener('click', async (event) => {
    const root = event.target.closest(ROOT_SELECTOR);
    if (!root) return;

    if (event.target.closest('[data-bundle-mobile-open]')) {
      const dialog = root.querySelector('[data-bundle-mobile-dialog]');
      setDialogIntent(root, 'options');
      syncOptionSheet(root);
      dialog.showModal();
    }

    if (event.target.closest('[data-bundle-mobile-buy-now]')) {
      const dialog = root.querySelector('[data-bundle-mobile-dialog]');
      setDialogIntent(root, 'buy-now');
      syncOptionSheet(root);
      dialog.showModal();
    }

    if (event.target.closest('[data-bundle-mobile-apply]')) {
      if (root.dataset.bundleMobileIntent === 'buy-now') {
        await buySelectedVariantNow(root);
      } else {
        pendingIntents.delete(root.dataset.sectionId);
        root.querySelector('[data-bundle-mobile-dialog]')?.close();
      }
    }

    if (event.target.closest('[data-bundle-mobile-close]')) {
      pendingIntents.delete(root.dataset.sectionId);
      root.querySelector('[data-bundle-mobile-dialog]')?.close();
    }
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-bundle-mobile-option-value-id]')) {
      const root = event.target.closest(ROOT_SELECTOR);
      applySheetSelectionToNativePicker(root);
      window.setTimeout(() => syncOptionSheet(root), 500);
      return;
    }

    if (event.target.matches('variant-selects input[type="radio"]')) {
      const root = event.target.closest('product-info')?.querySelector(ROOT_SELECTOR);
      if (root) {
        window.setTimeout(() => syncNativeDesktopPicker(root, true), 0);
        window.setTimeout(() => syncNativeDesktopPicker(root), 500);
      }
      return;
    }

    if (!event.target.matches('product-info form[id^="product-form-"] input[name="id"]')) return;

    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      syncStickyBar(root, event.target.value);
    });
  });

  const syncAllNativeDesktopPickers = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => syncNativeDesktopPicker(root, true));
  };

  syncAllNativeDesktopPickers();

  new MutationObserver((mutations) => {
    const pickerWasReplaced = mutations.some((mutation) =>
      [...mutation.addedNodes].some(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches?.('product-info, variant-selects') ||
            node.querySelector?.('product-info, variant-selects')),
      ),
    );
    if (pickerWasReplaced) {
      window.setTimeout(() => {
        syncAllNativeDesktopPickers();
        document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
          const intent = pendingIntents.get(root.dataset.sectionId);
          if (!intent || window.matchMedia('(min-width: 750px)').matches) return;
          setDialogIntent(root, intent);
          syncOptionSheet(root);
          const dialog = root.querySelector('[data-bundle-mobile-dialog]');
          if (dialog && !dialog.open) dialog.showModal();
        });
      }, 0);
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
